import clsx from "clsx";
import { useState, useEffect, useMemo, useRef } from "react";
import React from "react";
import classes from "./carousel.module.css";

const TABBABLE_SELECTORS = [
  "a[href]",
  "button",
  "input",
  "select",
  "textarea",
  "[tabindex]",
  '[role="button"]',
].join(",");

export const CarouselItem = ({
  children,
  className,
  isVisible,
}: {
  children: React.ReactNode;
  className?: string;
  isVisible: boolean;
}) => {
  const itemRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    if (!itemRef.current) return;

    const tabbableElements =
      itemRef.current.querySelectorAll(TABBABLE_SELECTORS);
    tabbableElements.forEach((element) => {
      if (element instanceof HTMLElement) {
        if (!isVisible) {
          element.tabIndex = -1;
        } else {
          element.removeAttribute("tabindex");
        }
      }
    });
  }, [isVisible]);

  return (
    <li
      ref={itemRef}
      className={clsx(classes.item, className)}
      aria-hidden={!isVisible}
    >
      {children}
    </li>
  );
};

export type CarouselProps = {
  children: React.ReactNode[];
  itemsToShow?: number;
  className?: string;
  gap?: number;
  nextIcon?: React.ReactNode;
  prevIcon?: React.ReactNode;
  prevAriaLabel?: string;
  nextAriaLabel?: string;
};

export const Carousel = ({
  children,
  itemsToShow = 1,
  className,
  gap = 0,
  nextIcon,
  prevIcon,
  prevAriaLabel = "View previous items",
  nextAriaLabel = "View next items",
}: CarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [windowWidth, setWindowWidth] = useState(0);
  const items = React.Children.toArray(children);
  const maxScroll = items.length - itemsToShow;

  useEffect(() => {
    if (!window) return;
    setWindowWidth(window.innerWidth);
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const translateX = useMemo(() => {
    const totalGapSpace = (itemsToShow - 1) * gap;
    const itemWidth = (100 - (totalGapSpace / windowWidth) * 100) / itemsToShow;
    return `-${currentIndex * (itemWidth + (gap / windowWidth) * 100)}%`;
  }, [itemsToShow, gap, windowWidth, currentIndex]);

  const handleNext = () => {
    setCurrentIndex((prev) => Math.min(prev + 1, maxScroll));
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => Math.max(prev - 1, 0));
  };

  const visibleIndices = Array.from(
    { length: itemsToShow },
    (_, i) => i + currentIndex,
  );

  return (
    <div className={classes.root}>
      {currentIndex > 0 && (
        <button
          onClick={handlePrevious}
          className={classes.navButton}
          aria-label={prevAriaLabel}
        >
          {prevIcon || "Previous"}
        </button>
      )}
      <div
        className={classes.carouselWrapper}
        style={
          {
            "--items-to-show": itemsToShow,
            "--gap": `${gap}px`,
          } as React.CSSProperties
        }
      >
        <ul
          className={clsx(classes.list, className)}
          style={{ "--translate-x": translateX } as React.CSSProperties}
        >
          {items.map((item, index) => (
            <CarouselItem
              key={index}
              isVisible={visibleIndices.includes(index)}
            >
              {item}
            </CarouselItem>
          ))}
        </ul>
      </div>
      {currentIndex < maxScroll && (
        <button
          onClick={handleNext}
          className={classes.navButton}
          aria-label={nextAriaLabel}
        >
          {nextIcon || "Next"}
        </button>
      )}
    </div>
  );
};
